/**
 * Wrapper main-thread do caminho WebCodecs (`webcodecsVideo.worker.ts`).
 *
 * Diferença importante para o wrapper do ffmpeg: aqui o worker é descartável.
 * Não há core de MBs para manter aquecido — os codecs são do sistema —, então
 * cada conversão sobe e derruba o seu, e um worker que morreu no meio não
 * contamina a próxima.
 */

export interface PedidoWebCodecs {
    blob: Blob;
    bitrateVideo: number;
    larguraMaxima: number | null;
    alvoBytes: number;
    codec: string;
}

export interface OpcoesWebCodecs {
    signal?: AbortSignal;
    onProgress?: (fraction: number) => void;
}

/** Sinaliza "este arquivo não serve para o caminho WebCodecs" — quem chama deve
 *  cair no ffmpeg.wasm SEM mostrar erro ao usuário. */
export class WebCodecsNaoSuportadoError extends Error {}

type MensagemDoWorker =
    | { type: 'progress'; value: number }
    | { type: 'done'; buffer: ArrayBuffer; remuxado: boolean }
    | { type: 'error'; message: string; naoSuportado?: boolean };

const DEBUG = (() => {
    const env = import.meta.env as Record<string, string | undefined>;
    if (env.VITE_FFMPEG_DEBUG === 'true') return true;
    try {
        return typeof localStorage !== 'undefined'
            && localStorage.getItem('whiz.ffmpegDebug') === 'true';
    } catch {
        return false;
    }
})();

export function converterViaWebCodecs(
    pedido: PedidoWebCodecs,
    opts: OpcoesWebCodecs = {},
): Promise<Blob> {
    const { signal, onProgress } = opts;

    return new Promise<Blob>((resolve, reject) => {
        const worker = new Worker(
            new URL('./webcodecsVideo.worker.ts', import.meta.url),
            { type: 'module' },
        );

        const encerrar = () => {
            worker.onmessage = null;
            worker.onerror = null;
            signal?.removeEventListener('abort', onAbort);
            worker.terminate();
        };
        const onAbort = () => {
            encerrar();
            reject(new DOMException('Conversão cancelada.', 'AbortError'));
        };

        if (signal) {
            if (signal.aborted) { onAbort(); return; }
            signal.addEventListener('abort', onAbort);
        }

        worker.onmessage = (e: MessageEvent<MensagemDoWorker>) => {
            const msg = e.data;
            if (msg.type === 'progress') {
                onProgress?.(msg.value);
            } else if (msg.type === 'done') {
                const blob = new Blob([msg.buffer], { type: 'video/mp4' });
                encerrar();
                resolve(blob);
            } else {
                encerrar();
                reject(
                    msg.naoSuportado
                        ? new WebCodecsNaoSuportadoError(msg.message)
                        : new Error(msg.message),
                );
            }
        };

        worker.onerror = (e) => {
            encerrar();
            // Worker que nem subiu (import quebrado, WebCodecs ausente no
            // worker scope) é caso de fallback, não de erro ao usuário.
            reject(new WebCodecsNaoSuportadoError(e.message || 'Worker WebCodecs falhou.'));
        };

        worker.postMessage({ type: 'convert', ...pedido, debug: DEBUG });
    });
}
